<template>
  <div class="inspection-editor">
    <div v-if="!hasSteps" class="inspection-editor__empty-state">
      <div class="inspection-editor__empty-inner">
        <div class="inspection-editor__empty-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M21 21L17.5001 17.5M20 11.5C20 16.1944 16.1944 20 11.5 20C6.80558 20 3 16.1944 3 11.5C3 6.80558 6.80558 3 11.5 3C16.1944 3 20 6.80558 20 11.5Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <p class="inspection-editor__empty-title">No Inspection Steps found</p>
        <p class="inspection-editor__empty-description">
          At least one Step is required to inspect for ability in this checkpoint
        </p>
        <div v-if="!readonly" class="inspection-editor__empty-actions">
          <StandardButton
            type="magenta"
            variant="primary"
            label="Add Step"
            left-icon="plusLeft"
            @click="openAddStepModal"
          />
          <AddORAskIQButton
            :show-add-button="false"
            :show-a-i-button="true"
            label="Suggest"
            emp-label="Add"
            :disabled="suggestDisabled"
            @addAI="emit('suggest', $event)"
          />
        </div>
      </div>
    </div>

    <template v-else>
      <div class="inspection-editor__filled">
        <div v-if="!readonly && !hideToolbar" class="inspection-editor__toolbar">
          <div class="inspection-editor__toolbar-left" />
          <div class="inspection-editor__toolbar-right">
            <EXTooltip
              v-if="isInspectionAtStepLimit"
              :title="INSPECTION_MAX_STEPS_TOOLTIP"
              placement="top"
              :delay="200"
            >
              <span class="inspection-editor__disabled-btn-tooltip-wrap">
                <StandardButton
                  type="magenta"
                  variant="secondary"
                  size="small"
                  label="Add Step"
                  left-icon="plusLeft"
                  disabled
                />
              </span>
            </EXTooltip>
            <StandardButton
              v-else
              type="magenta"
              variant="secondary"
              size="small"
              label="Add Step"
              left-icon="plusLeft"
              :disabled="isInspectionEditorBusy"
              @click="openAddStepModal"
            />
            <AddORAskIQButton
              :show-add-button="false"
              :show-a-i-button="true"
              label="Suggest"
              emp-label="Add"
              :disabled="suggestDisabled"
              @addAI="emit('suggest', $event)"
            />
            <MenuIcon
              v-if="hasSteps"
              class="inspection-editor__toolbar-menu"
              :vertical="true"
              :dropdown-items="protocolBulkMenuItems"
              :show-tooltip="false"
              :width="240"
              @onClick="onProtocolBulkMenuClick"
            />
          </div>
        </div>

        <div
          v-if="isInspectionAtStepLimit"
          class="inspection-editor__max-steps-banner"
          role="alert"
        >
          <span class="inspection-editor__max-steps-banner-icon" aria-hidden="true">
            <svg
              width="38"
              height="38"
              viewBox="0 0 38 38"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.3">
                <path
                  d="M19 6C26.1797 6 32 11.8203 32 19C32 26.1797 26.1797 32 19 32C11.8203 32 6 26.1797 6 19C6 11.8203 11.8203 6 19 6Z"
                  stroke="#D92D20"
                  stroke-width="2"
                />
              </g>
              <g opacity="0.1">
                <path
                  d="M19 1C28.9411 1 37 9.05887 37 19C37 28.9411 28.9411 37 19 37C9.05887 37 1 28.9411 1 19C1 9.05887 9.05887 1 19 1Z"
                  stroke="#D92D20"
                  stroke-width="2"
                />
              </g>
              <g clip-path="url(#inspection-max-steps-warning-clip)">
                <path
                  d="M19.0001 15.667V19.0003M19.0001 22.3337H19.0084M27.3334 19.0003C27.3334 23.6027 23.6025 27.3337 19.0001 27.3337C14.3977 27.3337 10.6667 23.6027 10.6667 19.0003C10.6667 14.398 14.3977 10.667 19.0001 10.667C23.6025 10.667 27.3334 14.398 27.3334 19.0003Z"
                  stroke="#D92D20"
                  stroke-width="1.66667"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <clipPath id="inspection-max-steps-warning-clip">
                  <rect width="20" height="20" fill="white" transform="translate(9 9)" />
                </clipPath>
              </defs>
            </svg>
          </span>
          <p class="inspection-editor__max-steps-banner-text">
            You have reached the maximum number of steps allowed in an inspection protocol
          </p>
        </div>

        <div v-if="newStepCardEnabled" class="inspection-editor__step-list">
          <InspectionStepEditorCard
            v-for="(step, stepIndex) in normalizedSteps"
            :key="step.__inspectionRowKey ?? `inspection-step-${stepIndex}`"
            :step-number="stepIndex + 1"
            :category="step.competence_category"
            :inspector-action="step.inspector_action"
            :look-for="step.look_for"
            :pass-criteria="step.pass_criteria"
            :fail-criteria="step.fail_criteria"
            :timestamp-ref="step.timestamp_ref"
            :video-jump-url="stepVideoJumpUrl(step)"
            :readonly="readonly"
            :disabled="isInspectionEditorBusy"
            @edit="openEditStepModal(stepIndex)"
            @delete="openDeleteStepDialog(stepIndex)"
            @timestamp-click="emit('timestamp-click', $event)"
          />
        </div>
        <div v-else class="inspection-editor__step-list">
          <div
            v-for="(step, stepIndex) in normalizedSteps"
            :key="step.__inspectionRowKey ?? `inspection-step-${stepIndex}`"
            class="inspection-editor__step-card-wrap"
          >
            <button
              v-if="!readonly"
              type="button"
              class="inspection-editor__icon-btn inspection-editor__icon-btn--edit"
              aria-label="Edit step"
              :disabled="isInspectionEditorBusy"
              @click="openEditStepModal(stepIndex)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
                class="inspection-editor__icon-btn-img"
              >
                <path
                  d="M8.75003 2.91403L11.0836 5.24733M12.3518 3.97124C12.6602 3.66291 12.8336 3.24469 12.8336 2.80859C12.8337 2.37249 12.6605 1.95423 12.3521 1.64582C12.0437 1.33741 11.6255 1.16412 11.1893 1.16406C10.7532 1.16401 10.3349 1.3372 10.0264 1.64553L2.24057 9.43231C2.10512 9.56735 2.00495 9.73361 1.94888 9.91647L1.17823 12.4551C1.16315 12.5055 1.16201 12.5591 1.17493 12.6102C1.18785 12.6612 1.21435 12.7078 1.25162 12.745C1.28888 12.7822 1.33552 12.8086 1.38659 12.8215C1.43766 12.8343 1.49125 12.8331 1.54168 12.8179L4.08115 12.0479C4.26385 11.9924 4.43012 11.8928 4.56536 11.758L12.3518 3.97124Z"
                  stroke="#344054"
                  stroke-width="2"
                />
              </svg>
            </button>
            <InstructionStepCard
              :competence_category="step.competence_category"
              :inspector_action="step.inspector_action"
              :look_for="step.look_for"
              :pass_criteria="step.pass_criteria"
              :fail_criteria="step.fail_criteria"
              :timestamp_ref="step.timestamp_ref"
              :video_jump_url="stepVideoJumpUrl(step)"
              class="inspection-editor__step-card inspection-editor__step-card--nested"
              @timestamp-click="emit('timestamp-click', $event)"
            />
          </div>
        </div>
      </div>
    </template>

    <InspectionStepDialog
      :value="inspectionModalOpen"
      :state="inspectionModalState"
      :category="modalCategory"
      :inspect="modalInspect"
      :look-for="modalLookFor"
      :pass-if="modalPassIf"
      :fail-if="modalFailIf"
      :timestamp-ref="modalTimestampRef"
      :show-timestamp="isVideoCheckpoint"
      :persistent="inspectionSubmitInProgress || inspectionDeleteInProgress"
      :is-submitting="inspectionSubmitInProgress"
      :is-deleting="inspectionDeleteInProgress"
      @input="onInspectionModalInput"
      @close="closeInspectionModal"
      @discard="closeInspectionModal"
      @submit="onInspectionModalSubmit"
      @delete="onInspectionModalDelete"
    />

    <DeleteInspectionProtocolDialog
      :dialog="deleteStepDialogOpen"
      :submitting="inspectionDeleteInProgress"
      title="Delete inspection step"
      :description="deleteStepDescription"
      warning-text="This action is irreversible"
      @close="closeDeleteStepDialog"
      @confirm="onConfirmDeleteStep"
    />

    <DeleteInspectionProtocolDialog
      :dialog="deleteProtocolDialogOpen"
      :submitting="deleteProtocolSubmitting"
      @close="onDeleteInspectionProtocolDialogClose"
      @confirm="onConfirmDeleteInspectionProtocol"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import AddORAskIQButton from "@components/AddORAskIQButton/AddORAskIQButton.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import SwitchButton from "@components/SwitchButton/SwitchButton.vue";
import InstructionStepCard from "@components/InstructionStepCard/InstructionStepCard.vue";
import InspectionStepEditorCard from "@components/InspectionStepEditorCard/InspectionStepEditorCard.vue";
import InspectionStepDialog from "@components/InspectionStepDialog/InspectionStepDialog.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import DeleteInspectionProtocolDialog from "@components/DeleteInspectionProtocolDialog/DeleteInspectionProtocolDialog.vue";
import { LD_FLAG_IDEA_MDP_648 } from "@components/constants/launchDarklyFlagKeys";

const props = defineProps({
  steps: {
    type: Array,
    default: null,
  },
  /**
   * Optional API-shaped protocol; when present with steps, editor still prefers `steps` prop for ordering.
   * Parent may derive `steps` from protocol the same way as quiz + questions.
   */
  protocol: {
    type: Object,
    default: null,
  },
  confirmEveryStep: {
    type: Boolean,
    default: false,
  },
  /** When true, hide Add Step / Suggest and other edit actions (e.g. checkpoint not locked by self). */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** When true, hide the filled-state toolbar (parent renders Add Step / Suggest on the quiz-mode row). */
  hideToolbar: {
    type: Boolean,
    default: false,
  },
  /** Disable Suggest when parent is readonly or inspection is skipped for this checkpoint. */
  suggestDisabled: {
    type: Boolean,
    default: false,
  },
  /** Base playable video URL for timestamp jumps when step.video_jump_url is missing (video checkpoints). */
  videoUrl: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "add-step",
  "update-step",
  /** `{ index, complete }` — parent calls `complete(err?)` after delete API. */
  "delete-step",
  "suggest",
  "update:confirmEveryStep",
  /** `{ complete(err?) }` — parent runs delete-protocol API, then calls `complete(null)` or `complete(err)`. */
  "delete-protocol-requested",
  /** Emitted when an inspection step timestamp link is clicked (video_jump_url). */
  "timestamp-click",
]);

let nextInspectionRowKey = 1;

function assignInspectionRowKeys(items) {
  return items.map((item) => ({
    ...item,
    __inspectionRowKey: item.__inspectionRowKey ?? nextInspectionRowKey++,
  }));
}

function normalizeCompetenceCategory(category) {
  const normalized = (category || "ability").toLowerCase();
  return normalized === "behavior" ? "behaviour" : normalized;
}

function parseTimestampToSeconds(timestampRef) {
  if (!timestampRef || typeof timestampRef !== "string") return 0;
  const first = timestampRef.split("-")[0].trim();
  const parts = first.split(":").map((part) => Number(part));
  if (parts.length === 2 && parts.every((n) => Number.isFinite(n))) {
    return parts[0] * 60 + parts[1];
  }
  if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return 0;
}

function buildVideoJumpUrl(baseUrl, timestampRef) {
  if (!baseUrl) return "";
  const seconds = parseTimestampToSeconds(timestampRef);
  const url = String(baseUrl).trim();
  if (!url) return "";

  if (/cloudflarestream\.com|videodelivery\.net/i.test(url)) {
    const withoutQuery = url.split("?")[0].split("#")[0];
    if (/\/iframe\/?$/i.test(withoutQuery)) {
      const separator = url.includes("?") ? "&" : "?";
      return `${withoutQuery}${separator}startTime=${seconds}`;
    }
    const uidMatch = withoutQuery.match(/\/([a-f0-9]{32})(?:\/iframe)?\/?$/i);
    if (uidMatch) {
      const origin = withoutQuery.replace(/\/[a-f0-9]{32}(?:\/iframe)?\/?$/i, "");
      return `${origin}/${uidMatch[1]}/iframe?startTime=${seconds}`;
    }
  }

  const baseWithoutHash = url.split("#")[0];
  return `${baseWithoutHash}#t=${seconds}`;
}

function stepVideoJumpUrl(step) {
  if (step.video_jump_url) return step.video_jump_url;
  if (props.videoUrl && step.timestamp_ref) {
    return buildVideoJumpUrl(props.videoUrl, step.timestamp_ref);
  }
  return props.videoUrl || "";
}

function mapIncomingStep(step = {}) {
  const seqRaw = step.step_sequence ?? step.step_no;
  const step_sequence =
    seqRaw != null && seqRaw !== "" && Number.isFinite(Number(seqRaw)) ? Number(seqRaw) : null;
  return {
    // IQ video-components / suggest paths persist the canonical Content-model
    // shape (step_type + what_to_look_for), while this editor works in the IAAD
    // shape. Mirror what_to_look_for into both prose fields (ER-12476).
    competence_category: normalizeCompetenceCategory(
      step.competence_category || step.competency_category || step.step_type
    ),
    inspector_action:
      step.inspector_action ||
      step.action ||
      step.chapter_title ||
      step.what_to_look_for ||
      step.look_for ||
      "",
    look_for:
      step.look_for ||
      step.chapter_description ||
      step.what_to_look_for ||
      step.inspector_action ||
      "",
    pass_criteria: step.pass_criteria ?? step.chapter_summary ?? "",
    fail_criteria: step.fail_criteria ?? "",
    timestamp_ref:
      step.timestamp_ref ??
      (step.start_time && step.end_time ? `${step.start_time} - ${step.end_time}` : ""),
    video_jump_url: step.video_jump_url ?? "",
    step_sequence,
  };
}

/** Unwrap `{ data: { … } }` to inner object when needed */
function resolveProtocolObject(protocolProp) {
  if (!protocolProp || typeof protocolProp !== "object") return null;
  if (Array.isArray(protocolProp.steps) || protocolProp.instructions) return protocolProp;
  if (protocolProp.data && typeof protocolProp.data === "object") return protocolProp.data;
  return protocolProp;
}

function extractStepsFromProtocol(protocolPayload) {
  const resolved = resolveProtocolObject(protocolPayload) ?? protocolPayload;
  if (!resolved || typeof resolved !== "object") return [];
  const nested = resolved.instructions?.steps;
  if (Array.isArray(nested) && nested.length) return nested;
  const deep = resolved.instructions?.instructions?.steps;
  if (Array.isArray(deep) && deep.length) return deep;
  const direct = resolved.inspection_instructions?.instructions?.steps;
  if (Array.isArray(direct) && direct.length) return direct;
  const directTop = resolved.inspection_instructions?.steps;
  if (Array.isArray(directTop) && directTop.length) return directTop;
  if (Array.isArray(resolved.steps_to_inspect) && resolved.steps_to_inspect.length) {
    return resolved.steps_to_inspect;
  }
  if (Array.isArray(resolved.steps) && resolved.steps.length) return resolved.steps;
  return [];
}

const orderedRawSteps = ref([]);

const inspectionModalOpen = ref(false);
const inspectionModalState = ref("Add");
const inspectionEditIndex = ref(-1);
/** Add-step / edit save: wait for API before closing dialog (parent calls `complete`). */
const inspectionSubmitInProgress = ref(false);
const inspectionDeleteInProgress = ref(false);

const { proxy } = getCurrentInstance() || {};
const newStepCardEnabled = ref(false);

onMounted(async () => {
  try {
    newStepCardEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_648, false)) === true;
  } catch {
    newStepCardEnabled.value = false;
  }
});

const deleteStepIndex = ref(-1);
const deleteStepDialogOpen = computed(() => deleteStepIndex.value >= 0);
const deleteStepDescription = computed(
  () => `You are about to delete step ${deleteStepIndex.value + 1} from this inspection protocol.`
);

const deleteProtocolDialogOpen = ref(false);
const deleteProtocolSubmitting = ref(false);

const modalCategory = ref("knowledge");
const modalInspect = ref("");
const modalLookFor = ref("");
const modalPassIf = ref("");
const modalFailIf = ref("");
const modalTimestampRef = ref("");

watch(
  () => [props.protocol, props.steps],
  () => {
    const explicit = Array.isArray(props.steps) ? props.steps : null;
    if (explicit && explicit.length > 0) {
      orderedRawSteps.value = assignInspectionRowKeys(explicit.map(mapIncomingStep));
      return;
    }
    const fromProtocol = extractStepsFromProtocol(props.protocol).map(mapIncomingStep);
    if (fromProtocol.length > 0) {
      orderedRawSteps.value = assignInspectionRowKeys([...fromProtocol]);
      return;
    }
    orderedRawSteps.value = [];
  },
  { immediate: true, deep: true }
);

function onConfirmEveryStepSwitchChange() {
  emit("update:confirmEveryStep", !props.confirmEveryStep);
}

function openAddStepModal() {
  if (isInspectionAtStepLimit.value) return;
  inspectionEditIndex.value = -1;
  inspectionModalState.value = "Add";
  modalCategory.value = "knowledge";
  modalInspect.value = "";
  modalLookFor.value = "";
  modalPassIf.value = "";
  modalFailIf.value = "";
  modalTimestampRef.value = "";
  inspectionModalOpen.value = true;
}

function openEditStepModal(stepIndex) {
  if (props.readonly || isInspectionEditorBusy.value) return;
  const raw = orderedRawSteps.value[stepIndex];
  if (!raw) return;
  inspectionEditIndex.value = stepIndex;
  inspectionModalState.value = "Edit";
  modalCategory.value = raw.competence_category || "knowledge";
  modalInspect.value = raw.inspector_action || "";
  modalLookFor.value = raw.look_for || "";
  modalPassIf.value = raw.pass_criteria || "";
  modalFailIf.value = raw.fail_criteria || "";
  modalTimestampRef.value = raw.timestamp_ref || "";
  inspectionModalOpen.value = true;
}

function closeInspectionModal() {
  if (inspectionSubmitInProgress.value || inspectionDeleteInProgress.value) return;
  inspectionModalOpen.value = false;
  inspectionEditIndex.value = -1;
}

function onInspectionModalInput(open) {
  if (!open && (inspectionSubmitInProgress.value || inspectionDeleteInProgress.value)) return;
  inspectionModalOpen.value = open;
  if (!open) inspectionEditIndex.value = -1;
}

function formPayloadToStep(form, preserve = {}) {
  return mapIncomingStep({
    competence_category: form.category,
    inspector_action: form.inspect,
    look_for: form.lookFor,
    pass_criteria: form.passIf,
    fail_criteria: form.failIf,
    timestamp_ref: form.timestamp_ref ?? preserve.timestamp_ref ?? "",
    // A jump URL built from the old timestamp would be stale once the timestamp is edited.
    video_jump_url:
      form.timestamp_ref !== undefined && form.timestamp_ref !== (preserve.timestamp_ref ?? "")
        ? ""
        : (preserve.video_jump_url ?? ""),
    step_sequence: preserve.step_sequence,
  });
}

function onInspectionModalSubmit(formData) {
  const isEdit = inspectionModalState.value === "Edit" || inspectionModalState.value === "Save";
  if (!isEdit && isInspectionAtStepLimit.value) return;
  const idx = inspectionEditIndex.value;
  const preserve =
    isEdit && idx >= 0 && orderedRawSteps.value[idx]
      ? {
          timestamp_ref: orderedRawSteps.value[idx].timestamp_ref,
          video_jump_url: orderedRawSteps.value[idx].video_jump_url,
          step_sequence: orderedRawSteps.value[idx].step_sequence,
        }
      : {};
  const step = formPayloadToStep(formData, preserve);
  if (isEdit && idx >= 0) {
    inspectionSubmitInProgress.value = true;
    emit("update-step", {
      index: idx,
      step,
      complete: (err) => {
        inspectionSubmitInProgress.value = false;
        if (!err) {
          inspectionModalOpen.value = false;
          inspectionEditIndex.value = -1;
        }
      },
    });
    return;
  }
  inspectionSubmitInProgress.value = true;
  emit("add-step", {
    step,
    complete: (err) => {
      inspectionSubmitInProgress.value = false;
      if (!err) {
        inspectionModalOpen.value = false;
        inspectionEditIndex.value = -1;
      }
    },
  });
}

function openDeleteStepDialog(stepIndex) {
  if (props.readonly || isInspectionEditorBusy.value) return;
  deleteStepIndex.value = stepIndex;
}

function closeDeleteStepDialog() {
  if (inspectionDeleteInProgress.value) return;
  deleteStepIndex.value = -1;
}

function onConfirmDeleteStep() {
  const idx = deleteStepIndex.value;
  if (idx < 0 || inspectionDeleteInProgress.value) return;
  inspectionDeleteInProgress.value = true;
  emit("delete-step", {
    index: idx,
    complete: (err) => {
      inspectionDeleteInProgress.value = false;
      if (!err) deleteStepIndex.value = -1;
    },
  });
}

function onInspectionModalDelete() {
  const idx = inspectionEditIndex.value;
  if (idx < 0) return;
  inspectionDeleteInProgress.value = true;
  emit("delete-step", {
    index: idx,
    complete: (err) => {
      inspectionDeleteInProgress.value = false;
      if (!err) {
        inspectionModalOpen.value = false;
        inspectionEditIndex.value = -1;
      }
    },
  });
}

const hasSteps = computed(() => orderedRawSteps.value.length > 0);

const MAX_INSPECTION_STEPS = 10;
const INSPECTION_MAX_STEPS_TOOLTIP =
  "You have reached the maximum number of steps allowed in an inspection protocol";

const isVideoCheckpoint = computed(() => Boolean(props.videoUrl));

const isInspectionAtStepLimit = computed(
  () => orderedRawSteps.value.length >= MAX_INSPECTION_STEPS
);

const isInspectionEditorBusy = computed(
  () =>
    inspectionSubmitInProgress.value ||
    inspectionDeleteInProgress.value ||
    deleteProtocolSubmitting.value
);

const normalizedSteps = computed(() => {
  if (!hasSteps.value) return [];
  return orderedRawSteps.value.map((row) => ({
    __inspectionRowKey: row.__inspectionRowKey,
    competence_category: row.competence_category,
    inspector_action: row.inspector_action,
    look_for: row.look_for,
    pass_criteria: row.pass_criteria,
    fail_criteria: row.fail_criteria,
    timestamp_ref: row.timestamp_ref ?? "",
    video_jump_url: row.video_jump_url ?? "",
  }));
});

const protocolBulkMenuItems = computed(() => {
  const busy =
    inspectionSubmitInProgress.value ||
    inspectionDeleteInProgress.value ||
    deleteProtocolSubmitting.value;
  return [
    {
      name: "Delete protocol",
      value: "delete-protocol",
      icon: "icons/dropdown/trash.svg",
      negative: true,
      destructive: true,
      disabled: busy,
    },
  ];
});

function onProtocolBulkMenuClick(item) {
  if (
    inspectionSubmitInProgress.value ||
    inspectionDeleteInProgress.value ||
    deleteProtocolSubmitting.value
  )
    return;
  if (item?.value !== "delete-protocol") return;
  deleteProtocolDialogOpen.value = true;
}

function onDeleteInspectionProtocolDialogClose() {
  if (deleteProtocolSubmitting.value) return;
  deleteProtocolDialogOpen.value = false;
}

function onConfirmDeleteInspectionProtocol() {
  if (deleteProtocolSubmitting.value) return;
  deleteProtocolSubmitting.value = true;
  emit("delete-protocol-requested", {
    complete: (err) => {
      deleteProtocolSubmitting.value = false;
      if (!err) {
        deleteProtocolDialogOpen.value = false;
      }
    },
  });
}

defineExpose({
  openAddStepModal,
  isInspectionAtStepLimit,
  isInspectionEditorBusy,
  protocolBulkMenuItems,
  onProtocolBulkMenuClick,
});
</script>

<style scoped>
.inspection-editor {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.inspection-editor__empty-state {
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  display: flex;
  padding: 35px 24px;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}

.inspection-editor__empty-inner {
  display: flex;
  max-width: 480px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}

.inspection-editor__empty-icon {
  border-radius: 10px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  display: flex;
  width: 48px;
  height: 48px;
  padding: 12px;
  justify-content: center;
  align-items: center;
}

.inspection-editor__empty-title {
  margin: 0;
  color: #101828;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.inspection-editor__empty-description {
  margin: 0;
  color: #667085;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.inspection-editor__empty-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
}

.inspection-editor__filled {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.inspection-editor__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.inspection-editor__toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1 1 280px;
}

.inspection-editor__toolbar-copy {
  color: #101828;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
}

.inspection-editor__toolbar-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.inspection-editor__disabled-btn-tooltip-wrap {
  display: inline-flex;
}

.inspection-editor__toolbar-menu {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.inspection-editor__toolbar-menu :deep(.ex-menu-icon) {
  margin: 0;
  padding: 10px 12px;
}

.inspection-editor__toolbar-menu :deep(.ex-menu-icon:hover) {
  margin: 0;
  padding: 10px 12px;
}

.inspection-editor__max-steps-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid #e4e7ec;
  background: #ffffff;
}

.inspection-editor__max-steps-banner-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inspection-editor__max-steps-banner-text {
  margin: 0;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.inspection-editor__switch-wrap {
  flex-shrink: 0;
}

.inspection-editor__switch-wrap :deep(.editing-switch-container) {
  left: 0;
  width: auto;
  justify-content: flex-start;
}

.inspection-editor__step-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.inspection-editor__step-card-wrap {
  position: relative;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  padding: 16px 18px;
  box-sizing: border-box;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.inspection-editor__step-card-wrap:hover {
  background: #f3f4ff;
}

.inspection-editor__icon-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
}

.inspection-editor__icon-btn:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 2px;
}

.inspection-editor__icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.inspection-editor__icon-btn-img {
  display: block;
  flex-shrink: 0;
  pointer-events: none;
}

.inspection-editor__icon-btn--edit {
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
}

.inspection-editor__icon-btn--edit:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #bfc8d4;
}

.inspection-editor__step-card--nested {
  pointer-events: none;
}

.inspection-editor__step-card--nested :deep(.instruction-step-card-timestamp-link) {
  pointer-events: auto;
  cursor: pointer;
}

.inspection-editor__step-card--nested :deep(.instruction-step-card-content-wrapper:hover) {
  background-color: transparent;
  cursor: default;
}

::v-deep .v-input--switch--inset .v-input--selection-controls__input {
  width: 38px;
}
</style>
